<template>
  <Tag
    v-if="meta"
    class="purchase-status-chip"
    :class="`purchase-status-chip--${meta.tone}`"
    :value="label"
    :severity="meta.severity"
  />
</template>

<script>
// The chip is a PrimeVue Tag, per the library-first rule, as OfferCard's pill
// is. The severity is the preset's solid fill for the same tone; the soft tint
// the design asks for is this chip's, set on the Tag's own root.
//
// The Tag is the template's only node, and has to stay that way: a comment
// beside it makes the template a fragment in development, and the Tag's root
// then loses this component's scope attribute and every rule in the style
// block with it.
//
// Each outcome's tint and the Tag severity that stands behind it.
//
// A plain `<script>` block rather than `<script setup>`: the map is the same for
// every chip, so it is built once for the module instead of once per instance.
const OUTCOMES = Object.freeze({
  won: { tone: 'success', severity: 'success' },
  if: { tone: 'warning', severity: 'warn' },
});
</script>

<script setup>
import { computed } from 'vue';
import Tag from 'primevue/tag';

import { isValidPurchaseStatusChipI18n, mergeTranslations } from '../../utils/purchaseStatusChipTranslations';

// Where one of the buyer's purchases stands, as a pill on its My Purchases
// card: "Purchased" once the asset is the buyer's, "If-Sale" while the seller
// decides whether to take a high bid that fell short of the reserve.
//
// Presentation only. It holds no state and reports nothing. The card around it
// and the hint beside it ("Seller deciding") are PurchaseCard's.
defineOptions({ name: 'PurchaseStatusChip' });

const props = defineProps({
  // Where the purchase stands: 'won' (the buyer bought the asset; the success
  // tint) | 'if' (an if-sale awaiting the seller's decision; the warning tint).
  // Each one's label is in ../../utils/purchaseStatusChipTranslations.js.
  outcome: {
    type: String,
    required: true,
    validator: (value) => ['won', 'if'].includes(value),
  },
  // `{ translations }`, partial and merged over the English defaults; see
  // ../../utils/purchaseStatusChipTranslations.js.
  i18n: { type: Object, default: null, validator: isValidPurchaseStatusChipI18n },
});

// Null for an outcome outside the set, which the validator has already warned
// about. The chip then draws nothing, rather than a word that would be wrong or
// a render error that would take the page's whole list down with it.
const meta = computed(() => (Object.hasOwn(OUTCOMES, props.outcome) ? OUTCOMES[props.outcome] : null));

// The outcome's label, in the reader's language.
const label = computed(() => mergeTranslations(props.i18n?.translations)[props.outcome]);
</script>

<style scoped>
/* On the Tag's own root, which carries this component's scope attribute, so
   these beat `.p-tag` and its severity class without `:deep()`. The label the
   Tag renders inside it has no rules of its own and inherits the type and the
   ink. The Tag already lays it out as the frame's pill does: `inline-flex`,
   centered.

   COMPONENTS.md's pill: 20 tall, with `0 7px` padding. The height is the
   SFC's, since a height is not a themable property. The label stays on one
   line and the chip keeps its width beside a hint, as the frame's pill does. */
.purchase-status-chip {
  height: 20px;
  flex-shrink: 0;
  padding: var(--p-purchase-status-chip-padding);
  border-radius: var(--p-purchase-status-chip-border-radius);
  font-size: var(--p-purchase-status-chip-font-size);
  font-weight: var(--p-purchase-status-chip-font-weight);
  white-space: nowrap;
}

/* The tone: a tint of the status hue for the ground, and the hue for the
   word. */
.purchase-status-chip--success {
  background: var(--p-purchase-status-chip-success-background);
  color: var(--p-purchase-status-chip-success-color);
}

.purchase-status-chip--warning {
  background: var(--p-purchase-status-chip-warning-background);
  color: var(--p-purchase-status-chip-warning-color);
}
</style>
